import { useState, type FormEvent } from 'react';
import { Button, Group, Modal, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useCreateOrganization, useOrganizations, type OrganizationBody } from '@/api/orgs';
import { EmptyState } from '@/components/EmptyState';
import { usePageTitle } from '@/components/usePageTitle';
import { OrganizationsTable } from '@/features/orgs/OrganizationsTable';
import { LOCALES, LOCALE_LABELS, type Locale } from '@/i18n';

export const Route = createFileRoute('/_staff/staff/organizations/')({ component: OrganizationsPage });

function OrganizationsPage() {
  const { t } = useTranslation();
  usePageTitle(t('nav.organizations'));
  const { data: orgs, isPending } = useOrganizations();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={1}>{t('nav.organizations')}</Title>
        <Button onClick={() => setCreateOpen(true)}>{t('staff.newOrganization')}</Button>
      </Group>
      {!isPending && orgs?.length === 0 ? (
        <EmptyState title={t('nav.organizations')} />
      ) : (
        <OrganizationsTable organizations={orgs ?? []} />
      )}
      <CreateOrganizationModal opened={createOpen} onClose={() => setCreateOpen(false)} />
    </Stack>
  );
}

function CreateOrganizationModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createOrganization = useCreateOrganization();
  const [name, setName] = useState('');
  const [defaultCrs, setDefaultCrs] = useState('');
  const [defaultLocale, setDefaultLocale] = useState<Locale>('cs');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setDefaultCrs('');
    setDefaultLocale('cs');
    setError(null);
  };
  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const body: OrganizationBody = {
      name,
      default_crs: defaultCrs || undefined,
      default_locale: defaultLocale,
    };
    try {
      const org = await createOrganization.mutateAsync(body);
      handleClose();
      await navigate({ to: '/staff/organizations/$orgId', params: { orgId: org.public_id } });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t('staff.newOrganization')}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          <TextInput
            label={t('org.name')}
            required
            withAsterisk={false}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <TextInput label={t('org.defaultCrs')} placeholder="EPSG:5514" value={defaultCrs} onChange={(e) => setDefaultCrs(e.currentTarget.value)} />
          <Select
            label={t('org.defaultLocale')}
            data={LOCALES.map((l) => ({ value: l, label: LOCALE_LABELS[l] }))}
            value={defaultLocale}
            allowDeselect={false}
            onChange={(v) => v && setDefaultLocale(v as Locale)}
          />
          {error && (
            <Text c="red" role="alert">
              {error}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={createOrganization.isPending}>
              {t('staff.newOrganization')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
